import { Controller } from "@hotwired/stimulus"

// Address autocomplete using OpenStreetMap's Nominatim service.
// No API key required, free for low-volume usage. We:
//   * Debounce keystrokes (350ms) so we don't hammer the endpoint.
//   * Keep the request limited to 5 results and US country bias.
//   * Cancel in-flight requests when the user keeps typing.
//   * On suggestion click: write the street address back AND fill the
//     sibling city / state / zip fields when present, so the cover
//     page chip + ZIP-resolved city stay in sync.
//
// Mounted on: the wizard's Street address input AND the editor pane's
// Street address input. Both bind to `client.address`. The autocomplete
// dispatches `input` events on the inputs it writes to so the existing
// plan-store sync notices the change.

// Geocode through our server proxy (/api/geocode). The proxy hides
// the Nominatim / ORS dance, caches per-query, and sidesteps the
// CORS-on-rate-limit edge case browsers run into when calling
// Nominatim's public tier directly.
const ENDPOINT = "/api/geocode"
const DEBOUNCE_MS = 350
const MIN_QUERY  = 4

export default class extends Controller {
  static targets = ["dropdown"]

  connect() {
    this._timer = null
    this._abort = null
    this._suggestions = []
    this.element.setAttribute("autocomplete", "off")
    this._onInput = (e) => this._query(e.target.value)
    this._onBlur  = () => setTimeout(() => this._hide(), 150)
    this._onFocus = () => { if (this._suggestions.length) this._show() }
    this.element.addEventListener("input", this._onInput)
    this.element.addEventListener("blur",  this._onBlur)
    this.element.addEventListener("focus", this._onFocus)
    this._buildDropdown()
  }

  disconnect() {
    this.element.removeEventListener("input", this._onInput)
    this.element.removeEventListener("blur",  this._onBlur)
    this.element.removeEventListener("focus", this._onFocus)
    if (this._timer) clearTimeout(this._timer)
    if (this._abort) this._abort.abort()
    this._dropdown?.remove()
  }

  _query(q) {
    if (this._timer) clearTimeout(this._timer)
    const trimmed = (q || "").trim()
    if (trimmed.length < MIN_QUERY) { this._hide(); return }
    this._timer = setTimeout(() => this._fetch(trimmed), DEBOUNCE_MS)
  }

  _fetch(q) {
    if (this._abort) this._abort.abort()
    this._abort = new AbortController()
    const url = `${ENDPOINT}?q=${encodeURIComponent(q)}`
    fetch(url, {
      signal: this._abort.signal,
      headers: { "Accept": "application/json", "Accept-Language": navigator.language || "en-US" }
    })
      .then(r => r.ok ? r.json() : { results: [] })
      .then(json => this._render(json?.results || []))
      .catch((err) => {
        // AbortError just means the user kept typing — ignore it. Any other
        // failure means the lookup is down; say so instead of a silent dead end.
        if (err && err.name === "AbortError") return
        this._message("Address lookup is unavailable right now — you can type the address in manually.")
      })
  }

  _render(results) {
    // California ONLY. The server already biases + filters to CA; this drops
    // anything non-CA that still slips through (e.g. a stale US-wide cache
    // entry, or a border result the bounding box let in).
    const stateOf = (s) => {
      const a = s.address || {}
      const code = (a.state_code || a["ISO3166-2-lvl4"] || "").toUpperCase()
      const name = (a.state || "").toLowerCase()
      if (code.endsWith("-CA") || code === "CA" || name === "california") return "CA"
      return code || name || ""
    }
    const list = (Array.isArray(results) ? results : []).filter(s => stateOf(s) === "CA")
    this._suggestions = list
    if (!this._dropdown) this._buildDropdown()
    if (!this._suggestions.length) {
      this._message("No California matches yet — keep typing, or enter the address manually.")
      return
    }
    this._dropdown.innerHTML = this._suggestions.map((s, i) => {
      const street = this._composeStreet(s.address)
      const locality = this._composeLocality(s.address)
      return `
        <button type="button" class="addr-ac-item" data-idx="${i}">
          <span class="addr-ac-street">${this._esc(street || s.display_name)}</span>
          ${locality ? `<span class="addr-ac-locality">${this._esc(locality)}</span>` : ""}
        </button>`
    }).join("")
    this._dropdown.querySelectorAll(".addr-ac-item").forEach(el => {
      el.addEventListener("mousedown", (e) => {
        e.preventDefault()
        const idx = parseInt(el.dataset.idx, 10)
        this._pick(this._suggestions[idx])
      })
    })
    this._position()
    this._show()
  }

  // Show a single non-clickable status line in the dropdown (no results / lookup
  // error) so a failed or empty lookup isn't a silent dead end for the user.
  _message(text) {
    if (!this._dropdown) this._buildDropdown()
    this._suggestions = []
    this._dropdown.innerHTML =
      `<div class="addr-ac-empty" style="padding:8px 12px;font-size:13px;color:var(--slate-500,#64748b);">${this._esc(text)}</div>`
    this._position()
    this._show()
  }

  _pick(s) {
    if (!s) return
    // The house number the user typed — OSM suggestions often drop it. Grab it
    // BEFORE we overwrite the input, to feed the precise Census resolve below.
    const typedHouseNo = ((this.element.value || "").match(/^\s*(\d+)/) || [])[1] || ""
    const a = s.address || {}
    const street = this._composeStreet(a) || (s.display_name || "").split(",")[0].trim()
    const city = a.city || a.town || a.village || a.hamlet || a.suburb || ""
    const state = a.state_code || this._stateToCode(a.state || "") || a.state || ""
    const zip = a.postcode || ""

    // Decide whether sibling fields exist. Look for plan-field bound
    // city/state/zip inputs on the page — they're the giveaway that
    // the user's expecting the address split across columns (the
    // Client section's wizard / editor). Otherwise we're a standalone
    // address field (e.g. Service Address inside a goal resource) and
    // should write the FULL composed address into the input so the
    // mileage lookup gets a precise geocode.
    const hasSiblingFields =
      document.querySelector("[data-plan-field='client.zip'],[data-plan-field='client.city']") != null

    if (hasSiblingFields) {
      this._set(this.element, street)
      if (zip)   this._fillByPlanField("client.zip",   zip)
      if (city)  this._fillByPlanField("client.city",  city)
      if (state) this._fillByPlanField("client.state", state)
    } else {
      // Compose "Street, City, ST ZIP" — full address into a single input.
      const locality = [city, state].filter(Boolean).join(", ")
      const full = [street, locality, zip].filter(Boolean).join(", ")
      this._set(this.element, full || (s.display_name || street))
    }

    this._suggestions = []
    this._hide()

    // OSM has the street + city but often no house number and the street's
    // general ZIP (e.g. 90808) instead of the specific one (90815). Upgrade to
    // a rooftop match (house number + correct ZIP) via the Census geocoder,
    // using the typed house number + the picked city/state. Fire-and-forget;
    // the OSM fill above stands if Census can't resolve it.
    this._resolvePrecise(typedHouseNo, street, city, state, hasSiblingFields)
  }

  _resolvePrecise(houseNo, street, city, state, hasSiblingFields) {
    const streetNoNum = String(street || "").replace(/^\s*\d+\s*/, "").trim()
    if (!houseNo || !streetNoNum || !city) return   // need a full address to match
    const oneline = `${houseNo} ${streetNoNum}, ${city}, ${state || "CA"}`
    fetch(`/api/geocode/resolve?address=${encodeURIComponent(oneline)}`, {
      headers: { "Accept": "application/json" }
    })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        const ra = j && j.result && j.result.address
        if (!ra) return
        const preciseStreet = this._composeStreet(ra) || street
        if (hasSiblingFields) {
          this._set(this.element, preciseStreet)
          if (ra.postcode)   this._fillByPlanField("client.zip",   ra.postcode)
          if (ra.city)       this._fillByPlanField("client.city",  ra.city)
          if (ra.state_code) this._fillByPlanField("client.state", ra.state_code)
        } else {
          const locality = [ra.city, ra.state_code].filter(Boolean).join(", ")
          this._set(this.element, [preciseStreet, locality, ra.postcode].filter(Boolean).join(", "))
        }
      })
      .catch(() => {})
  }

  _composeStreet(a) {
    if (!a) return ""
    const nr = (a.house_number || "").trim()
    const rd = (a.road || a.pedestrian || a.path || "").trim()
    if (nr && rd) return `${nr} ${rd}`
    return rd || ""
  }

  _composeLocality(a) {
    if (!a) return ""
    const city = a.city || a.town || a.village || a.hamlet || ""
    const st   = a.state_code || a.state || ""
    const zip  = a.postcode || ""
    return [city, st, zip].filter(Boolean).join(", ")
  }

  _set(input, value) {
    if (!input || input.value === value) return
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set
    if (setter) setter.call(input, value); else input.value = value
    input.dispatchEvent(new Event("input",  { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _fillByPlanField(path, value) {
    document.querySelectorAll(`[data-plan-field='${path}']`).forEach(el => this._set(el, value))
  }

  // Best-effort 2-letter US state code lookup. Returns "" for anything
  // unrecognized so the caller can fall back to the full name.
  _stateToCode(name) {
    const m = {
      alabama:"AL", alaska:"AK", arizona:"AZ", arkansas:"AR", california:"CA",
      colorado:"CO", connecticut:"CT", delaware:"DE", florida:"FL", georgia:"GA",
      hawaii:"HI", idaho:"ID", illinois:"IL", indiana:"IN", iowa:"IA", kansas:"KS",
      kentucky:"KY", louisiana:"LA", maine:"ME", maryland:"MD", massachusetts:"MA",
      michigan:"MI", minnesota:"MN", mississippi:"MS", missouri:"MO", montana:"MT",
      nebraska:"NE", nevada:"NV", "new hampshire":"NH", "new jersey":"NJ",
      "new mexico":"NM", "new york":"NY", "north carolina":"NC", "north dakota":"ND",
      ohio:"OH", oklahoma:"OK", oregon:"OR", pennsylvania:"PA", "rhode island":"RI",
      "south carolina":"SC", "south dakota":"SD", tennessee:"TN", texas:"TX",
      utah:"UT", vermont:"VT", virginia:"VA", washington:"WA", "west virginia":"WV",
      wisconsin:"WI", wyoming:"WY", "district of columbia":"DC"
    }
    return m[name.toLowerCase()] || ""
  }

  _buildDropdown() {
    if (this._dropdown) return
    this._dropdown = document.createElement("div")
    this._dropdown.className = "addr-ac-dropdown"
    this._dropdown.hidden = true
    document.body.appendChild(this._dropdown)
  }
  _position() {
    if (!this._dropdown) return
    const r = this.element.getBoundingClientRect()
    this._dropdown.style.position = "fixed"
    this._dropdown.style.top   = `${r.bottom + 2}px`
    this._dropdown.style.left  = `${r.left}px`
    this._dropdown.style.width = `${r.width}px`
    this._dropdown.style.zIndex = "9999"
  }
  _show() { if (this._dropdown) this._dropdown.hidden = false }
  _hide() { if (this._dropdown) this._dropdown.hidden = true }

  _esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
